import React, { useMemo } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';

import { Box, LinearProgress } from '@mui/material';

/**
 * Reusable Enterprise Data Table Component
 * Supports both server-side and client-side modes
 * 
 * @param {boolean} enableServerSide - If true, uses manual pagination/sorting/filtering (default: true)
 */
const AppDataTable = ({
    columns,
    data,
    rowCount,
    state,
    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,
    onColumnFiltersChange,
    isLoading = false,
    isRefetching = false,
    enableRowSelection = false,
    onRowSelectionChange,
    rowSelection,
    renderTopToolbarCustomActions,
    renderRowActions,
    muiTableBodyRowProps,
    // New props for client-side mode
    enableServerSide = true, // Default to server-side for backward compatibility
    enableRowVirtualization,
    enableGlobalFilter = true,
    enablePagination = true,
    enableSorting = true,
    enableMultiSorting = true,
    enableTopToolbar,
    initialPageSize = 10,
    initialSorting = [],
    emptyMessage = 'No data available',
    tableContainerHeight,
    tableContainerMinHeight,
    tableContainerFitContent = false,
    scrollContainer = true,
    unitName = 'records',
    getRowId,
    enableExpanding = false,
    getSubRows,
    muiTableHeadCellProps: muiTableHeadCellPropsOverride,
    muiTableBodyCellProps: muiTableBodyCellPropsOverride,
    muiTablePaperProps: muiTablePaperPropsOverride,
    muiTableContainerProps: muiTableContainerPropsOverride,
    onExpandedChange,
    icons,
}) => {
    const useRowVirtualization = enableRowVirtualization ?? enableServerSide;
    // Build state object based on mode
    const tableState = useMemo(() => {
        const baseState = {
            isLoading: isLoading,
            showProgressBars: isRefetching,
            rowSelection: rowSelection || {},
        };
        if (enableServerSide) {
            Object.assign(baseState, {
                pagination: state?.pagination,
                sorting: state?.sorting || [],
                globalFilter: state?.globalFilter || '',
                columnFilters: state?.columnFilters || [],
            });
        }
        if (state?.expanded !== undefined) {
            baseState.expanded = state.expanded;
        }
        return baseState;
    }, [enableServerSide, state, isLoading, isRefetching, rowSelection]);

    const tableConfig = useMemo(() => ({
        columns,
        data: data || [],
        rowCount: enableServerSide ? rowCount : undefined,

        state: tableState,

        // Server-side vs Client-side operations
        manualPagination: enableServerSide,
        manualSorting: enableServerSide,
        manualFiltering: enableServerSide,

        // Only pass handlers for server-side mode
        ...(enableServerSide && {
            onPaginationChange,
            onSortingChange,
            onGlobalFilterChange,
            onColumnFiltersChange,
        }),
        ...(onExpandedChange ? { onExpandedChange } : {}),

        // Initial state for client-side mode
        ...(!enableServerSide && {
            initialState: {
                pagination: { pageIndex: 0, pageSize: initialPageSize },
                sorting: initialSorting,
                density: 'comfortable',
                showGlobalFilter: true, // Show search bar by default
            },
        }),

        // Toolbar visibility based on mode
        enableTopToolbar: enableTopToolbar ?? (!enableServerSide && enableGlobalFilter),
        enableBottomToolbar: enablePagination,
        positionGlobalFilter: 'left',

        // Search/Filter settings
        enableGlobalFilter: enableGlobalFilter,
        enableGlobalFilterModes: true,
        enableFilters: true,
        enableColumnFilters: false,
        positionToolbarAlertBanner: 'none',

        // Search styling for client-side mode
        ...(!enableServerSide && {
            muiSearchTextFieldProps: {
                size: 'small',
                variant: 'outlined',
                sx: { minWidth: '300px' },
            },
        }),

        // Disable extra controls
        enableColumnActions: false,
        enableDensityToggle: false,
        enableColumnOrdering: false,
        enableHiding: false,
        enableFullScreenToggle: false,
        layoutMode: enableServerSide ? 'grid' : undefined,

        getRowId,
        enableExpanding,
        getSubRows,
        ...(enableExpanding && {
            getRowCanExpand: (row) => {
                if (row.original.canExpand !== undefined) return row.original.canExpand;
                return true;
            },
            muiExpandButtonProps: ({ row }) => ({
                sx: {
                    visibility: row.getCanExpand() ? 'visible' : 'hidden',
                    transform: row.getIsExpanded() ? 'rotate(0deg) !important' : 'rotate(-90deg) !important',
                    transition: 'transform 0.2s ease-in-out',
                }
            }),
            autoResetExpanded: false,
            enableExpandAll: false,
        }),

        // Virtualization (server-side by default; opt-in for client-side)
        ...(useRowVirtualization && {
            enableRowVirtualization: true,
            rowVirtualizerOptions: {
                overscan: 15,
                estimateSize: () => (enableServerSide ? 72 : 52),
            },
        }),

        // Sorting
        enableSorting: enableSorting,
        enableMultiSorting,
        maxMultiSortColCount: 3,
        enableSortingRemoval: true,

        // Pagination
        enablePagination: enablePagination,
        paginationDisplayMode: 'pages',
        muiPaginationProps: {
            color: 'primary',
            shape: 'rounded',
            variant: 'outlined',
            showFirstButton: true,
            showLastButton: true,
        },

        // Custom left-side bottom toolbar text (e.g., Showing 1 to X of Y tasks)
        renderBottomToolbarCustomActions: ({ table }) => {
            const { pagination } = table.getState();
            const total = table.getRowCount();
            const from = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
            const to = Math.min((pagination.pageIndex + 1) * pagination.pageSize, total);
            return (
                <Box sx={{ fontSize: '0.875rem', color: '#666', ml: 1, display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                    Showing {from} to {to} of {total} {unitName}
                </Box>
            );
        },

        // Container styling
        muiTableContainerProps: (props) => ({
            ...(typeof muiTableContainerPropsOverride === 'function' ? muiTableContainerPropsOverride(props) : (muiTableContainerPropsOverride || {})),
            sx: {
                minHeight: scrollContainer ? (tableContainerMinHeight || undefined) : undefined,
                maxHeight: scrollContainer ? (tableContainerHeight || undefined) : undefined,
                height: tableContainerFitContent ? 'auto' : undefined,
                flex: tableContainerFitContent ? 'none' : '1 1 auto',
                minWidth: 0,
                overflow: scrollContainer ? 'auto' : 'visible',
                ...(typeof muiTableContainerPropsOverride === 'function' ? muiTableContainerPropsOverride(props)?.sx : (muiTableContainerPropsOverride?.sx || {})),
            },
        }),

        // Body styling
        muiTableBodyProps: {
            sx: {
                minHeight: enableServerSide ? 300 : undefined,
            },
        },

        muiCircularProgressProps: {
            sx: {
                display: 'none'
            }
        },

        enableStickyHeader: true,

        // Row selection
        enableRowSelection,
        onRowSelectionChange,
        state: {
            ...tableState,
            columnPinning: state?.columnPinning || { right: ['mrt-row-actions'] },
            columnVisibility: state?.columnVisibility || {},
        },

        // Row actions
        enableRowActions: renderRowActions !== undefined ? !!renderRowActions : false,
        renderRowActions,
        positionActionsColumn: 'last',
        displayColumnDefOptions: {
            'mrt-row-expand': {
                header: '',
                size: 30,
            },
            'mrt-row-actions': {
                header: 'Actions',
                size: 110, // Ensure enough space for 3 icons
                muiTableBodyCellProps: {
                    sx: {
                        fontSize: '0.9rem',
                        color: '#212529',
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        textAlign: 'center',
                        px: 1,
                        backgroundColor: '#ffffff',
                    },
                },
                muiTableHeadCellProps: {
                    sx: {
                        backgroundColor: '#f8f9fa',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'center',
                        '& .Mui-TableHeadCell-Content': {
                            justifyContent: 'center',
                        },
                    },
                },
            },
        },

        // Custom toolbar actions
        renderTopToolbarCustomActions,

        icons,

        // Row props
        muiTableBodyRowProps: (props) => ({
            ...(typeof muiTableBodyRowProps === 'function' ? muiTableBodyRowProps(props) : muiTableBodyRowProps),
            sx: {
                boxShadow: 'none !important',
                ...(typeof muiTableBodyRowProps === 'function' && muiTableBodyRowProps(props)?.sx),
            },
        }),

        // Empty state for client-side
        ...(!enableServerSide && {
            renderEmptyRowsFallback: () => (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        color: '#6c757d',
                        fontSize: '0.95rem',
                    }}
                >
                    {emptyMessage}
                </Box>
            ),
        }),

        // Table paper styling for client-side
        muiTablePaperProps: {
            elevation: 0,
            ...(muiTablePaperPropsOverride || {}),
            sx: {
                borderRadius: '12px',
                border: enableServerSide ? 'none' : '1px solid #e9ecef',
                boxShadow: 'none',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                ...(muiTablePaperPropsOverride?.sx || {}),
            },
        },

        // Consistent heading and body cell styling for ALL modes
        muiTableHeadCellProps: {
            ...(muiTableHeadCellPropsOverride || {}),
            sx: {
                backgroundColor: '#f8f9fa',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #dee2e6',
                ...(muiTableHeadCellPropsOverride?.sx || {}),
            },
        },
        muiTableBodyCellProps: {
            ...(muiTableBodyCellPropsOverride || {}),
            sx: {
                fontSize: '0.9rem',
                color: '#212529',
                backgroundColor: '#ffffff',
                ...(muiTableBodyCellPropsOverride?.sx || {}),
            },
        },
    }), [
        columns, data, rowCount, tableState, enableServerSide, useRowVirtualization,
        onPaginationChange, onSortingChange, onGlobalFilterChange, onColumnFiltersChange,
        enableGlobalFilter, enablePagination, enableSorting, enableMultiSorting, enableTopToolbar,
        initialPageSize, getRowId, tableContainerFitContent, initialSorting,
        tableContainerMinHeight, scrollContainer,
        enableRowSelection, onRowSelectionChange, renderRowActions,
        renderTopToolbarCustomActions, muiTableBodyRowProps, emptyMessage, tableContainerHeight, unitName,
        muiTableHeadCellPropsOverride, muiTableBodyCellPropsOverride, muiTablePaperPropsOverride,
        muiTableContainerPropsOverride,
    ]);

    const table = useMaterialReactTable(tableConfig);

    return (
        <Box sx={{
            position: 'relative',
            flex: tableContainerFitContent ? 'none' : 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiPaper-root': {
                flex: tableContainerFitContent ? 'none' : 1,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'none',
            },
            '& .MuiTableContainer-root': {
                flex: tableContainerFitContent ? 'none' : 1,
            },
            '& .MuiTablePagination-root': {
                padding: '0 !important',
                minHeight: '32px !important',
            },
            '& .MuiTablePagination-toolbar': {
                minHeight: '32px !important',
                height: '32px !important',
                padding: '0 8px !important',
            },
            '& .MuiTablePagination-selectRoot': {
                margin: '0 !important',
            },
            '& .MuiTablePagination-actions': {
                margin: '0 0 0 8px !important',
            },
        }}>
            {(isRefetching || isLoading) && (
                <LinearProgress
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        height: '3px',
                        backgroundColor: 'transparent',
                    }}
                />
            )}
            <MaterialReactTable table={table} />
        </Box>
    );
};

export default AppDataTable;
